import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ControlTemplate } from '@/lib/control-templates';
import type { SettingsChange, WriteResult } from '@/lib/control-types';

interface PreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ControlTemplate | null;
  currentSettings: Record<string, unknown> | null;
  onConfirm: (changes: SettingsChange[]) => Promise<WriteResult | null>;
  writing: boolean;
}

export function PreviewDialog({
  open,
  onOpenChange,
  template,
  currentSettings,
  onConfirm,
  writing,
}: PreviewDialogProps) {
  const [result, setResult] = useState<WriteResult | null>(null);

  if (!template) return null;

  const changes = template.settings_changes.map((c) => ({
    ...c,
    previous_value: currentSettings?.[c.field] ?? 'unknown',
  }));

  const handleConfirm = async () => {
    const res = await onConfirm(changes);
    if (res) setResult(res);
  };

  const handleClose = () => {
    setResult(null);
    onOpenChange(false);
  };

  const riskColor = template.risk_level === 'high'
    ? 'text-wt-red-primary'
    : template.risk_level === 'medium'
      ? 'text-wt-amber-primary'
      : 'text-wt-green-primary';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-wt-bg-secondary border-wt-border-default sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            {template.name}
            {template.risk_level !== 'low' && (
              <AlertTriangle className={cn('h-4 w-4', riskColor)} />
            )}
          </DialogTitle>
          <DialogDescription className="text-wt-text-muted">
            {template.description}
          </DialogDescription>
        </DialogHeader>

        {/* Result state */}
        {result ? (
          <div className="space-y-3">
            <div className={cn(
              'flex items-center gap-2 p-3 rounded-md border',
              result.verification_passed
                ? 'bg-wt-green-primary/5 border-wt-green-primary/20'
                : 'bg-wt-amber-primary/5 border-wt-amber-primary/20',
            )}>
              {result.verification_passed ? (
                <CheckCircle2 className="h-5 w-5 text-wt-green-primary flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-wt-amber-primary flex-shrink-0" />
              )}
              <div>
                <p className="text-sm font-medium text-wt-text-primary">
                  {result.verification_passed ? 'Settings applied and verified' : 'Settings written with verification warnings'}
                </p>
                {result.fields_written.length > 0 && (
                  <p className="text-xs text-wt-text-muted mt-0.5">
                    Fields written: {result.fields_written.join(', ')}
                  </p>
                )}
                {result.verification_errors.length > 0 && (
                  <ul className="text-xs text-wt-amber-primary mt-1 space-y-0.5">
                    {result.verification_errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {result.fields_rejected.length > 0 && (
              <div className="p-3 rounded-md border bg-wt-red-primary/5 border-wt-red-primary/20">
                <p className="text-xs font-medium text-wt-red-primary mb-1">Rejected fields:</p>
                {result.fields_rejected.map((r, i) => (
                  <p key={i} className="text-xs text-wt-text-muted">
                    <span className="font-mono text-wt-red-primary">{r.field}</span>: {r.reason}
                  </p>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} className="border-wt-border-default text-wt-text-primary">
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {/* Diff preview */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-wt-text-secondary uppercase tracking-wider">
                Changes to apply
              </p>
              <div className="rounded-md border border-wt-border-subtle bg-wt-bg-primary divide-y divide-wt-border-subtle">
                {changes.map((change) => (
                  <div key={change.field} className="px-3 py-2 flex items-center justify-between gap-4">
                    <span className="text-xs font-mono text-wt-text-secondary">{change.field}</span>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-wt-text-muted">
                        {change.previous_value !== undefined && change.previous_value !== null
                          ? String(change.previous_value)
                          : '—'}
                      </span>
                      <span className="text-wt-text-disabled">&rarr;</span>
                      <span className="font-mono text-wt-text-primary font-medium">
                        {change.value !== null ? String(change.value) : 'computed'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={writing}
                className="border-wt-border-default text-wt-text-primary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={writing}
                className={cn(
                  template.risk_level === 'high'
                    ? 'bg-wt-red-primary hover:bg-wt-red-primary/90'
                    : template.risk_level === 'medium'
                      ? 'bg-wt-amber-primary hover:bg-wt-amber-primary/90'
                      : 'bg-wt-green-primary hover:bg-wt-green-primary/90',
                  'text-white',
                )}
              >
                {writing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Writing...
                  </>
                ) : (
                  'Confirm & Apply'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
